import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { t } from "i18next";
import { useSearchParams } from "react-router-dom";

import {
  Headings,
  PrimaryButton,
  SecondaryButton,
} from "@sentara-health/optimahealth_web";
import {
  MemberSearchRecord,
  MemberSearchResponse,
  useEmployerIdentificationCard,
  useEmployerIdentificationCardRequest,
} from "@sentara-health/sentara-api-hooks-core";

import {
  DeviceTypes,
  SERVICE_NUMBER,
  listPerTable,
} from "utils/constants/Constants";
import { useAuth, useGlobalState } from "providers";
import SearchTableDeskTop from "./SearchTableDeskTop";
import { REQUEST_MEMBER_ID_CARD } from "routes";
import { getKey } from "utils/helpers/RequestMemberId";
import Pagination from "organisms/pagination/pagination";
import {
  IdRequestType,
  OrderIDList,
  SelectedDepType,
} from "utils/models/CommonTypes";
import { ModalOverlayPopUp } from "organisms/popup/ModalOverlayPopUp";
import SearchTableMobile from "./SearchTableMobile";

interface SearchProps {
  memberList: MemberSearchResponse | null;
}

const RequestMemberIDCardSearch = (props: SearchProps) => {
  const { user } = useAuth();
  const { getIdentificationCard } = useEmployerIdentificationCard();
  const { getIdentificationCardRequest } =
    useEmployerIdentificationCardRequest();
  const { setPageLoader } = useGlobalState();
  const { memberList } = props;
  const navigate = useNavigate();
  let [searchParams] = useSearchParams();
  const [searchFields, setSearchFields] = useState([]);
  const [openPopup, setOpenPopup] = useState(false);
  const [popupData, setPopupData] = useState("");
  const [pageList, setPageList] = useState<OrderIDList>();
  const [selectedDep, setSelectedDep] = useState<SelectedDepType>();
  const [page, setPage] = useState<number>(1);
  const { deviceType } = useGlobalState();
  const [popupStackContent, setPopupStackContent] = useState<any>([]);
  const isMobile = deviceType === DeviceTypes.MOBILE;

  const cardRequest = async () => {
    let content = "";
    if (!selectedDep || !Object.keys(selectedDep).length) {
      setOpenPopup(true);
      content = t("noMemberSelected");
      setPopupData(content);
      return;
    }
    const _requsted: IdRequestType = {
      success: [],
      pending: [],
      failed: [],
    };

    if (user?.loginID) {
      for (const i in selectedDep) {
        const item: any = selectedDep[i];

        for (const id of item) {
          setPageLoader("getIdentificationCardRequest", true);
          const res = await getIdentificationCardRequest({
            memberId: id,
            loginId: user?.loginID,
          });
          setPageLoader("getIdentificationCardRequest", false);
          if (res?.error && res?.errorCode) {
            if (res?.errorCode.toString() === "409") {
              _requsted.pending.push(id);
            } else {
              _requsted.failed?.push(id);
            }
          } else if (res?.data) {
            _requsted.success.push({ id: id, code: res?.data });
          } else {
            _requsted.failed?.push(id);
          }
        }
      }
    }

    let key = getKey(_requsted);
    setOpenPopup(true);
    let stack = [];
    setPopupStackContent([]);

    if (_requsted.success.length > 1) {
      stack.push({
        content: t(`requestMemberIDCard.request.scenario_1Multi`),
        memberId: _requsted.success,
      });
      setPopupStackContent(stack);
    }

    content = t(`requestMemberIDCard.request.${key}`, {
      memberId: _requsted?.success[0]?.id,
      confirmationCode: _requsted?.success[0]?.code,
      memberIdFailed: _requsted?.failed?.join(", "),
      memberIdPending: _requsted?.pending?.join(", "),
      serviceNo: SERVICE_NUMBER.EMPLOYER_SERVICE_NUMBER,
      serviceNo1: SERVICE_NUMBER.EMPLOYER_SERVICE_NUMBER_1,
    });
    setPopupData(content);
  };

  const printParamValue = (key: string) => {
    if (key === "accountId") {
      const accountId = searchParams.get(key);
      // Find account name from user's groups
      const account = user?.groups?.find(
        (group) => group.accountId === accountId
      );
      // Return both account ID and account name if found, otherwise just account ID
      return account?.accountName
        ? `${accountId} ${account.accountName}`
        : accountId;
    }
    return ["firstName", "lastName"].includes(key)
      ? "'" + searchParams.get(key) + "'"
      : searchParams.get(key);
  };

  useEffect(() => {
    let params: any = [];
    searchParams.forEach((item: string, key: string) => {
      if (!["page", "checkboxAllMembers"].includes(key)) {
        if ("groupId" === key && !item) {
          if (
            !params.length ||
            ["on", "true"].includes(
              searchParams.get("checkboxAllMembers") ?? ""
            )
          ) {
            const groups: Array<string> = [];
            user?.groups?.forEach((element, i) => {
              if (i < 25) {
                groups.push(element.groupID.toString());
              }
            });
            params[0] = `${t("requestMemberIDCard.allMemberGroups")} ${groups.join(", ")}...`;
          }
        } else if (item) {
          if ("groupId" === key) {
            if (
              !params.length ||
              ["on", "true"].includes(
                searchParams.get("checkboxAllMembers") ?? ""
              )
            ) {
              params[0] = `${t("requestMemberIDCard.allMemberGroups")} ${item}`;
            }
          } else {
            if (
              (params[0] as string)?.indexOf(
                t("requestMemberIDCard.allMemberGroups")
              ) > -1 &&
              !["on", "true"].includes(
                searchParams.get("checkboxAllMembers") ?? ""
              )
            ) {
              params = params.slice(1);
            }
            params.push(
              `${key === "lastName" ? t("enrollmentHistoryForm.memberWith") : ""} ${t("enrollmentHistoryForm.key." + key)} ${printParamValue(key)}`
            );
          }
        }
      }
    });
    setSearchFields(params);
  }, [searchParams]);

  useEffect(() => {
    const list: any = {};
    memberList?.Result?.forEach((item: MemberSearchRecord) => {
      if (item.memberId) {
        const id = `${item.memberId.split("*")[0]}+dep`;
        list[item.memberId] = {
          dateOfBirth: item.dateOfBirth,
          firstName: item.firstName,
          midName: item.midName,
          lastName: item.lastName,
          relationshipCode: item.relationshipCode,
        };
        if (!list[id]) {
          list[id] = [];
        }
        list[id].push(item.memberId);
      }
    });
    Object.keys(list).forEach((key) => {
      if (Array.isArray(list[key]) && list[key].length < 2) {
        delete list[key];
      }
    });
    setPageList(list);
  }, [memberList?.Result]);

  const searchFieldsJoin = (searchFields: string[]) => {
    const last = searchFields.length > 1 ? searchFields.pop() : "";
    return last
      ? `${searchFields.join(", ")} and ${last}`
      : searchFields.join(", ");
  };

  return (
    <div>
      <Headings
        className="redhat_semibold fw-600 font-twentyfour black-color mb-4"
        level={2}
        text={t("requestMemberIDCard.searchResult")}
        dataTestId="txtRequestMemberIdCardSearchResultHeader"
      />
      <div
        className="mt-3 mb-4"
        data-testid="lblRequestMemberIdCardYouSearchedFor"
      >
        <strong>{t("enrollmentHistoryForm.searchFor")}</strong>{" "}
        <span>{searchFields.join(" and ")}.</span>
      </div>
      <SecondaryButton
        data-testid="btnRequestMemberIdCardNewSearch"
        className={"secondary_btn btn_block_mob mb-4"}
        onClick={() => navigate(`/app/${REQUEST_MEMBER_ID_CARD}`)}
      >
        {t("requestMemberIDCard.newSearch")}
      </SecondaryButton>
      <div className="d-flex flex-column gap-3 p-4 border-radius10 mb-4 light-grey-bg border">
        <div>
          <strong>{t("requestMemberIDCard.notes")}</strong>{" "}
          {t("requestMemberIDCard.notesContent")}
          {t("requestMemberIDCard.notesContent1")}
          {t("requestMemberIDCard.notesContent2")}
        </div>
      </div>
      <ModalOverlayPopUp
        open={openPopup}
        title={
          selectedDep && Object.keys(selectedDep).length
            ? t("requestMemberIDCard.request.modalTitle")
            : ""
        }
        displayButton={true}
        content={popupData}
        contentStack={popupStackContent}
        callBack={() => setOpenPopup(false)}
      />

      <div className="row mb-4">
        <div className="col-lg-12 col-md-12 col-sm-12 col-12">
          {isMobile ? (
            <SearchTableMobile
              {...{
                pageList,
                selectedDep,
                page,
                setSelectedDep,
                getIdentificationCard,
              }}
            />
          ) : (
            <SearchTableDeskTop
              {...{
                pageList,
                selectedDep,
                page,
                setSelectedDep,
                getIdentificationCard,
              }}
            />
          )}
          <div className="d-flex align-items-center pt-2 pb-2 ps-2">
            {pageList && (
              <Pagination
                total={Object.keys(pageList).length ?? 0}
                records={listPerTable}
                current={page ?? 1}
                callBack={(page: any) => {
                  setPage(page);
                  const ele = document.querySelector(
                    ".table-responsive"
                  ) as HTMLDivElement;
                  ele?.scrollIntoView({ behavior: "smooth" });
                }}
              />
            )}
          </div>
        </div>
      </div>
      <div className="mb-5">
        <PrimaryButton
          data-testid="btnRequestMemberIdCard"
          id="primary"
          type="submit"
          className={
            selectedDep && Object.keys(selectedDep).length
              ? "primary_btn btn_block_mob"
              : "primary_btn disabled_btn btn_block_mob"
          }
          aria-label={
            selectedDep && Object.keys(selectedDep).length
              ? t("requestMemberIDCard.orderCard")
              : t("requestMemberIDCard.orderCardDisabled")
          }
          onClick={() => {
            cardRequest();
          }}
        >
          {t("requestMemberIDCard.orderCard")}
        </PrimaryButton>
      </div>
    </div>
  );
};
export default RequestMemberIDCardSearch;
